import {useParams} from "react-router-dom";
import {Helmet} from "react-helmet-async";
import {
    Badge,
    Box,
    Button, ButtonGroup,
    Card,
    CircularProgress,
    Container, Paper,
    Stack,
    Table, TableBody,
    TableCell, TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";
import {GeneralLayout} from "../layouts/GeneralLayout";
import Scrollbar from "../components/scrollbar";
import {
    useEditAttestationGradeMutation,
    useGetAttestationDetailsQuery,
    useGetAttestationStatementDetailsQuery
} from "../store/attestation/attestationApi";
import {useEffect, useState} from "react";
import PageTitle from "../components/PageTitle";
import IconButton from "@mui/material/IconButton";
import PrintIcon from "@mui/icons-material/Print";
import TextField from "@mui/material/TextField";
import copyObject from "../utils/copyObject";

export default function AttestationDetailsPage() {
    const {id} = useParams();
    const isAdmin = localStorage.getItem('roles').split(',').includes('AttNewAdmin');
    const [isLoading, setIsLoading] = useState(false);
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [resultsByGroup, setResultsByGroup] = useState(null);
    const [disciplines, setDisciplines] = useState([]);
    const {data} = useGetAttestationDetailsQuery({attestationId: id});
    const resultsByGroupQuery = useGetAttestationStatementDetailsQuery({attestationId: id, groupId: selectedGroupId});
    const [reportIsLoading, setReportIsLoading] = useState(false);
    const [refresh, setRefresh] = useState(false);
    const [copyOfData, setCopyOfData] = useState(null);
    const [editAttestationGradeRequest] = useEditAttestationGradeMutation();

    const stickyStyle = {
        position: 'sticky',
        left: 0,
        zIndex: 1,
        backgroundColor: '#FFF', // белый фон, чтобы избегать полупрозрачности
    };
    
    const cellStyle = { backgroundColor: 'white', top: 0, zIndex: 10 };
    
    const headerStyle = {
        height: 'calc(100vh - 275px)',   // ← свободное пространство
    }
    
    useEffect(() => {
        if (selectedGroupId === null || selectedGroupId === undefined)
            return;
        
        setIsLoading(true);

        resultsByGroupQuery.refetch().unwrap().then((fulfilled) => {
            setIsLoading(false);
            setResultsByGroup(copyObject(fulfilled?.students));
            setDisciplines(copyObject(fulfilled?.disciplines));
        });
    }, [selectedGroupId])
    
    function avg(array) {
        if (array === null || array === undefined || array.length === 0)
            return '-';
        
        if (array === 0)
            return 0;
        
        return Math.round(array.reduce((a, b) => a + b, 0) / array.length * 100) / 100;
    }
    
    function avgByDiscipline(disciplineId) {
        const grades = resultsByGroup.map((s) => {
            return s.results.find((sr) => {
                return sr.disciplineId === disciplineId
            })?.gradeId
        }).filter((g) => g !== null && g !== undefined && g >= 2 && g <= 5);
        
        return avg(grades);
    }
    
    function avgByStudent(student) {
        const grades = student.results.filter(g => {
            return g?.gradeId !== null && g.gradeId >= 2 && g.gradeId <= 5;
        }).map((item) => item.gradeId);
        
        const missedHours = student.results
            .filter((item) => item.missedHours !== null)
            .map((item) => item.missedHours);
        
        return `${avg(grades)}`; // /${missedHours.reduce((a, b) => a + b, 0)}`;
    }
    
    function gradeIdToColor(gradeId) {
        if (gradeId === null)
            return 'info';
        
        switch (gradeId){
            case 1: return 'info';
            case 2: return 'error';
            case 3: return 'warning';
            case 4: return 'success';
            case 5: return 'success';
            case 6: return 'error';
            case 7: return 'error';
            default: return 'info';
        }
    }
    
    function gradeIdToGradeName(gradeId) {
        switch (gradeId) {
            case 1: return  '-';
            case 6: return  'н/д';
            case 7: return  'н/я';
            default: return gradeId.toString();
        }
    }
    
    function onGroupSelected(groupId) {
        setSelectedGroupId(groupId);
    }
    
    function applyVisualChanges() {
        setRefresh(!refresh);
    }
    
    function handleGradeClick(resultOfThisDiscipline, studentId) {
        if (!isAdmin)
            return;
        
        resultOfThisDiscipline.editMode = true;
        applyVisualChanges();
    }
    
    async function printStatement() {
        setReportIsLoading(true);
        fetch(`/Reports/AttestationReport?groupId=${selectedGroupId}&&attestationId=${id}&fileType=xlsx`,
            {
                method: "GET",
                headers: {
                    "semesterid": localStorage.getItem("selectedSemesterId"),
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${localStorage.getItem('token')}`
                }
            }).then(response => {
            setReportIsLoading(false);
            return response.blob();
        }).then(response => {
            if (response.size < 1024*4) { // отсекаем все, что ниже 4кб
                alert('Не удалось загрузить файл')
                return;
            }

            const url = window.URL.createObjectURL(
                new Blob([response]),
            );
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute(
                'download',
                `Аттестация.xlsx`,
            );

            // Append to html link element page
            document.body.appendChild(link);

            // Start download
            link.click();

            // Clean up and remove the link
            link.parentNode.removeChild(link);
        }).catch((e) => {
            alert('Ошибка')
        })
    }
    
    async function setGradeByAdmin(newGrade, resultOfThisDiscipline, studentId) {
        if (!isAdmin)
            return;

        resultOfThisDiscipline.editMode = false;
        resultOfThisDiscipline.isLoading = false;
        resultOfThisDiscipline.gradeId = parseInt(newGrade, 10);
        applyVisualChanges();

        // TODO: сохранение
        await editAttestationGradeRequest({data: {
                attestationId: id,
                disciplineId: resultOfThisDiscipline.disciplineId,
                studentId,
                gradeId: parseInt(newGrade, 10)
            }}).unwrap().then((fulfilled) => {
            if (fulfilled?.success) {
                // resultOfThisDiscipline.editMode = false;
                // resultOfThisDiscipline.isLoading = false;
                // resultOfThisDiscipline.gradeId = parseInt(newGrade, 10);
                // applyVisualChanges();
            } else {
                fulfilled.errors.map((item) => {
                    alert(item);
                    return 1;
                });
            }
        });
    }

    return (
        <GeneralLayout>
            <PageTitle title={'Аттестационные ведомости'} />

            <Container>
                <Stack direction="column"
                       padding="120px 0px 0px 100px"
                       spacing={2}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
                        <Typography variant="h4" gutterBottom>
                            {data === null || data === undefined ? <CircularProgress /> : <></>}
                            Аттестационные ведомости
                        </Typography>
                    </Stack>

                    <Stack direction={'row'} justifyContent={'space-between'} fullWidth>
                        <Stack direction="row" spacing={1}>
                            {data?.data?.groups?.map((item) => {
                                return <Button variant="contained" key={item.groupId} onClick={() => { onGroupSelected(item.groupId) }}>{item.groupName}</Button>
                            })}
                        </Stack>

                        {disciplines?.length > 0 
                        ? <IconButton onClick={() => {printStatement()}}
                                      size={'small'}
                                      disabled={reportIsLoading}>
                                {reportIsLoading
                                ? <CircularProgress size={15}/>
                                : <PrintIcon />}
                            </IconButton>
                        : <></>}
                    </Stack>
                    
                    <Card>
                        <Scrollbar>
                            {disciplines?.length > 0 
                            ? <Box>
                                <TableContainer component={Paper} sx={headerStyle}>
                                    <Table size={'small'} stickyHeader>
                                        <TableHead style={{'zIndex': 999}}>
                                            <TableRow>
                                                <TableCell width={10}>#</TableCell>
                                                <TableCell>ФИО студента</TableCell>
                                                {disciplines?.map((d) => {
                                                    return <TableCell align="center" key={d.disciplineName}>{d.disciplineName}</TableCell>
                                                })}
                                                <TableCell align={"center"}><strong>ИТОГО</strong></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {resultsByGroup?.map((result, index) => {
                                                return <TableRow>
                                                    <TableCell align="center">
                                                        <Typography>
                                                            {index + 1}
                                                            <Box bgcolor="#1a90ff"
                                                                 color="white"
                                                                 borderRadius={2}
                                                                 padding={0.5}>
                                                                <Typography fontSize={"small"}>
                                                                    {result.results.filter((r) => {
                                                                        return r.gradeId === 2 || r.gradeId > 5;
                                                                    }).length}
                                                                    /
                                                                    {result.results.length}
                                                                </Typography>
                                                            </Box>
                                                        </Typography>
                                                    </TableCell>

                                                    <TableCell style={stickyStyle}>
                                                        <strong>{result.studentLastName}</strong><br />{result.studentFirstName} {result.studentMiddleName}
                                                    </TableCell>
                                                    {disciplines?.map((discipline) => {
                                                        const resultOfThisDiscipline = result?.results?.find((r) => r.disciplineId === discipline.disciplineId);
                                                        return <TableCell align="center" style={cellStyle}>
                                                            <Badge
                                                                anchorOrigin={{
                                                                    vertical: 'bottom',
                                                                    horizontal: 'right',
                                                                }}
                                                                badgeContent={resultOfThisDiscipline?.missedHours}
                                                                color="primary">
                                                                {!resultOfThisDiscipline?.editMode
                                                                    ? <Button variant="contained"
                                                                              onClick={() => {handleGradeClick(resultOfThisDiscipline, result.studentId);}}
                                                                              color={gradeIdToColor(resultOfThisDiscipline?.gradeId)}
                                                                              size="small">
                                                                        {
                                                                            resultOfThisDiscipline !== null && resultOfThisDiscipline !== undefined
                                                                                ? gradeIdToGradeName(resultOfThisDiscipline.gradeId)
                                                                                : '-'
                                                                        }
                                                                    </Button>
                                                                    : <>
                                                                        {resultOfThisDiscipline.isLoading
                                                                            ? <CircularProgress size={15}/>
                                                                            : <ButtonGroup orientation={'horizontal'}
                                                                                           onClick={(event) => {setGradeByAdmin(event.target.value, resultOfThisDiscipline, result.studentId)}}
                                                                                           size={'small'}>
                                                                                <Button value={5}>5</Button>
                                                                                <Button value={4}>4</Button>
                                                                                <Button value={3}>3</Button>
                                                                                <Button value={2}>2</Button>
                                                                                <Button value={7}>н/я</Button>
                                                                                <Button value={6}>н/д</Button>
                                                                                <Button value={1}>-</Button>
                                                                            </ButtonGroup>}
                                                                    </>
                                                                }
                                                            </Badge>
                                                        </TableCell>
                                                    })}
                                                    <TableCell align={"center"}>{avgByStudent(result)}</TableCell>
                                                </TableRow>
                                            })}
                                            <TableRow>
                                                <TableCell colSpan={2} align={"right"}><strong>ИТОГО</strong></TableCell>
                                                {disciplines?.map((d) => {
                                                    return <TableCell align={"center"}>{avgByDiscipline(d.disciplineId)}</TableCell>
                                                })}
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                </Box>
                            : <>{isLoading ? <CircularProgress /> : <></>}</>}
                        </Scrollbar>
                    </Card>
                    
                    <br />
                </Stack>
            </Container>
        </GeneralLayout>)
}